import { useLayoutEffect, useRef } from 'react';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useTheme } from '@styledComponents';
import { useIsMounted } from '@hooks/useIsMounted';
import ScrollMagic from 'scrollmagic';
import { gsap, Linear, Sine } from 'gsap';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useLottieAnimation } from '@hooks/useLottieAnimation';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';

export const useAllTogetherNowSectionController = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const partyAnimationContainerRef = useRef<HTMLDivElement>(null);
  const partyAnimationElRef = useRef<HTMLDivElement>(null);
  const textBlock1Ref = useRef<HTMLDivElement>(null);
  const textBlock2Ref = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const theme = useTheme();
  const isMounted = useIsMounted();
  const isMobile = useThemeMediaQuery('below_sm');
  const { animationRef, loadAnimation } = useLottieAnimation(
    {
      path: cloudAssetUrl('wedding-invitations/unified-rsvp/bodymovin/data.json'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      name: 'all together now',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: partyAnimationElRef }
  );

  useLayoutEffect(() => {
    let scene1: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let scene2: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let scene3: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let timeline1: ReturnType<typeof gsap.timeline>;
    let timeline2: ReturnType<typeof gsap.timeline>;
    let timeline3: ReturnType<typeof gsap.timeline>;
    const sectionEl = sectionRef.current;
    const tableWrapperEl = tableWrapperRef.current;
    const tableEl = tableRef.current;
    const firstTextBlockEl = sectionEl?.querySelector('#all-together-first-text-block') as HTMLElement;
    const secondTextBlockEl = sectionEl?.querySelector('#all-together-second-text-block') as HTMLElement;
    const partyAnimationContainerEl = partyAnimationContainerRef.current;
    const partyAnimationEl = partyAnimationElRef.current;
    if (scrollController && isMounted && sectionEl && tableEl && tableWrapperEl && partyAnimationContainerEl) {
      const getPartyTl1EndFrame = 89;
      const getPartyTl2EndFrame = 165;
      const getPartyTl3Part1EndFrame = 240;
      const initTimelines = () => {
        timeline1 = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
        timeline1.to(
          { frame: 0 },
          {
            duration: 1.5,
            onUpdate: function() {
              animationRef.current?.goToAndStop(Math.round(this.progress() * getPartyTl1EndFrame), true);
            },
          },
          0
        );
        timeline1.to(
          tableEl,
          {
            duration: 1.5,
            y: -350,
          },
          0
        );
        //
        timeline2 = gsap.timeline({ paused: true, default: { ease: Linear.easeNone } });
        timeline2.to(
          { frame: 0 },
          {
            duration: 1,
            onUpdate: function() {
              animationRef.current?.goToAndStop(
                Math.round(getPartyTl1EndFrame + this.progress() * (getPartyTl2EndFrame - getPartyTl1EndFrame)),
                true
              );
            },
          },
          0
        );
        timeline2.to(
          tableEl,
          {
            duration: 1,
            y: isMobile ? -1400 : -1200,
          },
          0
        );
        if (isMobile) {
          timeline2.to(
            partyAnimationContainerEl,
            {
              duration: 0.2,
              x: '-45vw',
              scale: 0.7,
            },
            0
          );
          timeline2.to(
            firstTextBlockEl,
            {
              duration: 0.15,
              opacity: 0,
              y: 55,
              ease: Sine.easeInOut,
            },
            0.55
          );
          timeline2.to(
            partyAnimationEl,
            {
              duration: 0.2,
              x: '-60%',
            },
            0.8
          );
        }
        timeline2.to(
          partyAnimationContainerEl,
          {
            duration: 1,
            y: isMobile ? 720 : 925,
          },
          0
        );
        //
        timeline3 = gsap.timeline({ paused: true, default: { ease: Linear.easeNone } });
        timeline3.to(
          { frame: 0 },
          {
            duration: 1,
            onUpdate: function() {
              animationRef.current?.goToAndStop(
                Math.round(getPartyTl2EndFrame + this.progress() * (getPartyTl3Part1EndFrame - getPartyTl2EndFrame)),
                true
              );
            },
          },
          0
        );
        timeline3.call(() => {
          animationRef.current?.goToAndPlay(getPartyTl3Part1EndFrame, true);
        });
        timeline3.to(
          partyAnimationContainerEl,
          {
            duration: 0.2,
            x: 100,
            ease: Sine.easeInOut,
          },
          0
        );
      };

      const initScenes = () => {
        scene1 = new ScrollMagic.Scene({
          triggerElement: tableWrapperEl,
          triggerHook: 0.5,
          offset: 200,
          duration: 700,
        })
          .setPin(tableWrapperEl, { pushFollowers: false })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            timeline1?.progress(event.progress);
          });
        scene2 = new ScrollMagic.Scene({
          triggerElement: tableWrapperEl,
          triggerHook: 0.5,
          offset: 900,
          duration: isMobile ? 780 : 1200,
        })
          .setPin(tableWrapperEl, { pushFollowers: false })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            timeline2?.progress(event.progress);
          });
        scene3 = new ScrollMagic.Scene({
          triggerElement: tableWrapperEl,
          triggerHook: 0.5,
          offset: isMobile ? 1680 : 2100,
          duration: isMobile ? 300 : 680,
        })
          .setPin(tableWrapperEl, { pushFollowers: false })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            timeline3?.progress(event.progress);
          });
      };
      initTimelines();
      initScenes();
    }
    const clearScene = () => {
      timeline1?.progress(0).kill();
      timeline2?.progress(0).kill();
      timeline3?.progress(0).kill();
      //
      scene1?.destroy(true);
      scene2?.destroy(true);
      scene3?.destroy(true);
    };
    return () => {
      clearScene();
    };
  }, [scrollController, isMounted, theme, animationRef, isMobile]);
  const initAssets = () => {
    loadAnimation();
  };
  return {
    sectionRef,
    tableWrapperRef,
    tableRef,
    partyAnimationContainerRef,
    partyAnimationElRef,
    textBlock1Ref,
    textBlock2Ref,
    initAssets,
  };
};
